import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { GlobalFactorySettings } from './GlobalFactorySettings';

/**
 * FactorySettingsModal
 * --------------------------------------------------------------------
 * Modal premium para parâmetros globais da fábrica (turnos, minutos por
 * turno, dias úteis/mês). Apenas envelopa o componente standalone
 * `<GlobalFactorySettings />` — toda lógica de estado vive lá.
 *
 * Características:
 *  - Backdrop-blur sutil (não escurece o canvas de forma agressiva)
 *  - Header limpo: título + botão de fechar
 *  - Fecha com ESC ou clique no backdrop
 *  - Renderização condicional (não monta nada quando fechado) → zero
 *    impacto em FPS do canvas ReactFlow quando ocioso
 */
interface FactorySettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const FactorySettingsModal: React.FC<FactorySettingsModalProps> = ({
  isOpen,
  onClose,
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Parâmetros da Fábrica"
      className="fixed inset-0 z-[100] flex items-center justify-center"
    >
      {/* Backdrop com blur sutil */}
      <button
        type="button"
        aria-label="Fechar modal"
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm cursor-default"
      />

      {/* Card do modal */}
      <div
        className="relative z-10 w-full max-w-md mx-4 rounded-xl bg-white dark:bg-slate-900
                   border border-slate-200/80 dark:border-slate-700/70
                   shadow-2xl shadow-slate-900/10
                   animate-in fade-in zoom-in-95 duration-150"
      >
        <header className="flex items-center justify-between px-5 py-3 border-b border-slate-200/70 dark:border-slate-700/60">
          <div>
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              Parâmetros da Fábrica
            </h2>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
              Configuração global aplicada a todos os cálculos do mapa.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100
                       dark:hover:text-slate-200 dark:hover:bg-slate-800
                       transition-colors"
          >
            <X size={16} />
          </button>
        </header>

        <div className="p-5">
          <GlobalFactorySettings />
        </div>
      </div>
    </div>
  );
};
